import React from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Stat, StatusBadge } from "./AdminWidgets";

/**
 * Meta CAPI deduplication diagnostic panel.
 * Renders 3 KPI cards + a table of pixel/server event pairs.
 */
export default function DedupPanel({ dedup }) {
  const pairs = dedup?.pairs || [];
  const dedupCount = pairs.filter((p) => p.deduplicated).length;
  return (
    <div data-testid="admin-dedup-panel" className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat label="Eventi server (CAPI)" value={dedup?.server?.length || 0} />
        <Stat label="Eventi pixel (browser)" value={dedup?.pixel?.length || 0} />
        <Stat label="Coppie deduplicate" value={`${dedupCount} / ${pairs.length}`} accent />
      </div>

      <div className="bg-white rounded-2xl border border-[#e3eaf2] overflow-hidden shadow-[0_15px_50px_-30px_rgba(26,42,58,0.35)]">
        <div className="p-5 border-b border-[#eef2f7]">
          <p className="text-sm" style={{ color: "var(--np-muted)" }}>
            Ogni evento Meta è inviato due volte — dal browser (Pixel) e dal server (CAPI) — con lo
            <strong> stesso <code>event_id</code></strong>. Meta deduplica automaticamente le coppie.
            Se entrambe le colonne <em>Pixel</em> e <em>Server</em> sono <span className="text-emerald-600">verdi</span>, la deduplicazione avverrà.
          </p>
        </div>
        {pairs.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm" style={{ color: "var(--np-muted)" }}>
            Nessun evento ancora. Apri la landing in un&apos;altra scheda, accetta i cookie e clicca un CTA per generare un evento di test.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-[#f7fafd] hover:bg-[#f7fafd]">
                <TableHead className="font-semibold text-[12px] tracking-wider uppercase" style={{ color: "var(--np-primary)" }}>Quando</TableHead>
                <TableHead className="font-semibold text-[12px] tracking-wider uppercase" style={{ color: "var(--np-primary)" }}>Evento</TableHead>
                <TableHead className="font-semibold text-[12px] tracking-wider uppercase" style={{ color: "var(--np-primary)" }}>event_id</TableHead>
                <TableHead className="font-semibold text-[12px] tracking-wider uppercase" style={{ color: "var(--np-primary)" }}>Server (CAPI)</TableHead>
                <TableHead className="font-semibold text-[12px] tracking-wider uppercase" style={{ color: "var(--np-primary)" }}>Pixel (browser)</TableHead>
                <TableHead className="font-semibold text-[12px] tracking-wider uppercase" style={{ color: "var(--np-primary)" }}>Dedup</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pairs.map((p) => (
                <TableRow key={p.event_id} className="align-top hover:bg-[#fafcfe]">
                  <TableCell className="whitespace-nowrap text-sm" style={{ color: "var(--np-muted)" }}>{p.server?.at ? new Date(p.server.at).toLocaleString("it-IT") : "—"}</TableCell>
                  <TableCell className="text-sm font-medium" style={{ color: "var(--np-primary)" }}>{p.event_name}</TableCell>
                  <TableCell><code className="text-[12px] bg-[#f0f4f8] px-2 py-1 rounded">{p.event_id?.slice(0, 8)}…{p.event_id?.slice(-4)}</code></TableCell>
                  <TableCell><StatusBadge ok={p.server?.status === "ok"} label={p.server?.status || "—"} /></TableCell>
                  <TableCell><StatusBadge ok={p.pixel?.fired} label={p.pixel?.fired ? "fired" : "non-fired"} /></TableCell>
                  <TableCell>
                    {p.deduplicated ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                        <CheckCircle2 size={12} /> Verrà deduplicato
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                        <XCircle size={12} /> Solo server
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="rounded-2xl border border-[#dbe3ec] bg-[#f7fafd] p-5 text-sm leading-relaxed" style={{ color: "#2a3a4a" }}>
        <p className="font-semibold mb-2" style={{ color: "var(--np-primary)", fontFamily: "Merriweather, serif" }}>Come verificare la deduplicazione su Meta</p>
        <ol className="list-decimal list-inside space-y-1">
          <li>Apri <a className="underline" href="https://business.facebook.com/events_manager2" target="_blank" rel="noreferrer">Events Manager</a> → seleziona il pixel <code>1817378319234579</code>.</li>
          <li>Vai su <em>Overview → Events Deduplication</em>: vedrai le coppie Browser+Server riconciliate.</li>
          <li>Per i test in tempo reale: usa la tab <em>Test Events</em>, copia il codice (es. <code>TESTxxxxx</code>) nel <code>.env</code> come <code>META_TEST_EVENT_CODE</code>, riavvia il backend e ripeti i clic.</li>
        </ol>
      </div>
    </div>
  );
}
